import { useDispatch, useSelector } from 'react-redux';
import { stageSelector } from '../store/reducers/stageSlice/stageSlice';
import { charterSelector } from '../store/reducers/charterSlice/charterSlice';
import { positionSelector } from '../store/reducers/positionSlice/positionSlice';
import { useUpdateAll } from './useUpdateAll';
import moment from 'moment';
import { gantActions } from '../store/reducers/gantSlice/gantSlice';
import { queueFetch } from '../helpers/queueFetch';
import { positionsConnectionsActions } from '../store/reducers/positionsConnections/positionsConnectionsSlice';

export const useAutoPlanningMhd = () => {
    const { updateAll, getStageChildren } = useUpdateAll();

    const dispatch = useDispatch();
    const { settings } = useSelector((state) => state.gantReducer);
    const stages = useSelector(stageSelector.selectAll);
    const chartersEntities = useSelector(charterSelector.selectEntities);
    const positions = useSelector(positionSelector.selectAll);
    const positionsEntities = useSelector(positionSelector.selectEntities);

    const isNotMHD = !positions.some(
        (position) => position.type !== 'labor' && !position.planned.start_date
    );

    const addConnect = (type, positionTo, positionFrom, autOPlanningForm) => {
        const connection = {
            to: positionTo.guid,
            from: positionFrom.guid,
            type,
        };

        const plannedMHD = {
            days_count: autOPlanningForm.days,
            end_date: moment(positionFrom.planned.start_date).add(-1, 'days').format(),
            start_date: moment(positionFrom.planned.start_date)
                .add(-+autOPlanningForm.days, 'days')
                .format(),
        };

        const updatedPositionTo = {
            ...positionTo,
            planned: plannedMHD,
            connections: [...positionTo.connections, connection],
        };
        const updatedPositionFrom = {
            ...positionFrom,
            connections: [...positionFrom.connections, connection],
        };

        return { updatedPositionTo, updatedPositionFrom };
    };

    const autoPlanningMhd = (autOPlanningForm) => {
        const queue = [];
        const queueConnections = [];

        const stagesStarts = stages
            .filter((stage) => stage.planned.start_date)
            .map((stage) => stage.planned.start_date)
            .sort(
                (positionA, positionB) => moment(positionB).valueOf() - moment(positionA).valueOf()
            );

        const resultEntities = { ...positionsEntities };
        dispatch(gantActions.changeSettings({ ...settings, isShowMHD: true }));
        stages.forEach((stage) => {
            const { allPositions } = getStageChildren(positionsEntities, stage);

            const firstStagePosition = [...allPositions]
                .filter((position) => position.type === 'labor')
                .filter((position) => position.planned.start_date)
                .sort(
                    (positionA, positionB) =>
                        moment(positionA.planned.end_date).valueOf() -
                        moment(positionB.planned.end_date).valueOf()
                )[0];

            if (firstStagePosition) {
                const stagePositions = stage.positions
                    .filter((stageChildGuid) => !chartersEntities[stageChildGuid])
                    .map((guid) => resultEntities[guid]);

                stagePositions.forEach((position) => {
                    if (position?.type !== 'labor' && position.planned.start_date === null) {
                        const { updatedPositionTo, updatedPositionFrom } = addConnect(
                            1,
                            resultEntities[position.guid],
                            resultEntities[firstStagePosition.guid],
                            autOPlanningForm
                        );

                        queueConnections.push({
                            positionFromGuid: updatedPositionFrom.guid,
                            positionToGuid: updatedPositionTo.guid,
                            connectionType: 1,
                        });

                        queue.push({
                            data: {
                                data: {
                                    guid: updatedPositionTo.guid,
                                    startDate: moment(updatedPositionTo.planned.start_date).format(
                                        'YYYY-MM-DD'
                                    ),
                                    endDate: moment(updatedPositionTo.planned.end_date).format(
                                        'YYYY-MM-DD'
                                    ),
                                },
                                type: 'update',
                                action: 'auto_planning',
                            },
                            subEntityType: 'step',
                            subEntityId: stage.id,
                            t: Date.now(),
                        });

                        resultEntities[updatedPositionTo.guid] = updatedPositionTo;
                        resultEntities[updatedPositionFrom.guid] = updatedPositionFrom;
                    }
                });

                const stageCharters = stage.positions
                    .filter((stageChildGuid) => !!chartersEntities[stageChildGuid])
                    .map((guid) => chartersEntities[guid]);

                stageCharters.forEach((charter) => {
                    const charterPosition = charter.children.map((guid) => resultEntities[guid]);
                    const firstCharterLabor = charterPosition
                        .filter((position) => position.type === 'labor')
                        .filter((position) => position.planned.start_date)
                        .sort(
                            (positionA, positionB) =>
                                moment(positionA.planned.end_date).valueOf() -
                                moment(positionB.planned.end_date).valueOf()
                        )[0];

                    const positionProm = firstCharterLabor ? firstCharterLabor : firstStagePosition;

                    charterPosition.forEach((position) => {
                        if (position?.type !== 'labor' && position.planned.start_date === null) {
                            const { updatedPositionTo, updatedPositionFrom } = addConnect(
                                1,
                                resultEntities[position.guid],
                                resultEntities[positionProm.guid],
                                autOPlanningForm
                            );

                            queueConnections.push({
                                positionFromGuid: updatedPositionFrom.guid,
                                positionToGuid: updatedPositionTo.guid,
                                connectionType: 1,
                            });

                            queue.push({
                                data: {
                                    data: {
                                        guid: updatedPositionTo.guid,
                                        startDate: moment(
                                            updatedPositionTo.planned.start_date
                                        ).format('YYYY-MM-DD'),
                                        endDate: moment(updatedPositionTo.planned.end_date).format(
                                            'YYYY-MM-DD'
                                        ),
                                    },
                                    type: 'update',
                                    action: 'auto_planning',
                                },
                                subEntityType: 'step',
                                subEntityId: stage.id,
                                t: Date.now(),
                            });

                            resultEntities[updatedPositionTo.guid] = updatedPositionTo;
                            resultEntities[updatedPositionFrom.guid] = updatedPositionFrom;
                        }
                    });
                });
            } else {
                const positionsMHD = allPositions.filter(
                    (position) => position.type !== 'labor' && !position.planned.start_date
                );

                const start_date = stagesStarts[0]
                    ? stagesStarts[0]
                    : moment().hour(0).minute(0).seconds(0).format();
                const days_count = autOPlanningForm.days;

                const end_date = moment(start_date)
                    .add(+days_count - 1, 'days')
                    .format();

                const planned = {
                    start_date,
                    end_date,
                    days_count,
                };

                positionsMHD.forEach((position) => {
                    resultEntities[position.guid] = { ...position, planned };
                    queue.push({
                        data: {
                            data: {
                                guid: position.guid,
                                startDate: moment(planned.start_date).format('YYYY-MM-DD'),
                                endDate: moment(planned.end_date).format('YYYY-MM-DD'),
                            },
                            type: 'update',
                            action: 'auto_planning',
                        },
                        subEntityType: 'step',
                        subEntityId: stage.id,
                        t: Date.now(),
                    });
                });
            }
        });

        const newConnections = queueConnections.map((connection) => ({
            from: connection.positionFromGuid,
            to: connection.positionToGuid,
            type: connection.connectionType,
        }));

        const dataForQueueConnection = [
            {
                data: {
                    data: {
                        connection: queueConnections,
                    },
                    type: 'save_connections',
                    action: 'auto_planning',
                },
                subEntityType: 'step',
                subEntityId: stages[0].id,
                t: Date.now(),
            },
        ];
        queueFetch(queue);
        queueFetch(dataForQueueConnection);
        updateAll(resultEntities);
        dispatch(positionsConnectionsActions.addConnections(newConnections));
    };
    return { autoPlanningMhd, isNotMHD, stages };
};
